import { Container, Graphics, Text } from "pixi.js";
import type { PartyMarker } from "@frontier/protocol/contracts";
import { worldToScreen } from "./geometry";
import type { Point } from "./geometry";
import { navigationSafeRect } from "./navigation-layout";
import { markerOverlap, partyBadgePosition } from "./party-marker-layout";
import type { ScreenRect } from "./party-marker-layout";

export class PartyMarkerView {
  readonly world = new Container();
  readonly screen = new Container();
  private readonly pin = new Graphics();
  private readonly badgeBackground = new Graphics();
  private readonly direction = new Graphics();
  private readonly label = new Text({
    text: "",
    style: {
      fontFamily: "system-ui, sans-serif",
      fontSize: 11,
      fill: 0xc6a5fc,
      align: "center",
      wordWrap: true,
      wordWrapWidth: 180,
    },
  });
  private readonly edgeLabel = new Text({
    text: "",
    style: {
      fontFamily: "system-ui, sans-serif",
      fontSize: 10,
      fill: 0xc6a5fc,
      wordWrap: true,
      wordWrapWidth: 165,
    },
  });

  constructor() {
    this.world.label = "party-marker-world";
    this.screen.label = "party-marker-screen";
    this.label.anchor.set(0.5, 0);
    this.label.y = 29;
    this.world.addChild(this.pin, this.label);
    this.screen.addChild(this.badgeBackground, this.direction, this.edgeLabel);
  }

  render(
    marker: PartyMarker | null,
    camera: Point,
    width: number,
    height: number,
    zoom: number,
    now: number,
    occupied?: ScreenRect,
    tutorialGuidanceVisible = false,
  ): void {
    this.world.visible = false;
    this.screen.visible = false;
    if (!marker) return;
    const point = worldToScreen(marker, camera, width, height, zoom);
    const distance = Math.round(
      Math.hypot(marker.x - camera.x, marker.y - camera.y),
    );
    const safe = navigationSafeRect(width, height, tutorialGuidanceVisible);
    this.label.text = `ГРУППА · ${marker.label}`;
    const labelRect = {
      x: point.x - this.label.width / 2,
      y: point.y + 29,
      width: this.label.width,
      height: this.label.height,
    };
    this.label.visible =
      labelRect.x > safe.left &&
      labelRect.x + labelRect.width < safe.right &&
      labelRect.y > safe.top &&
      labelRect.y + labelRect.height < safe.bottom &&
      (!occupied || !markerOverlap(labelRect, occupied));
    if (
      point.x > -40 &&
      point.x < width + 40 &&
      point.y > -40 &&
      point.y < height + 40
    ) {
      this.world.visible = true;
      this.world.position.set(marker.x, marker.y);
      this.world.scale.set(1 / zoom);
      const radius = 22 + Math.sin(now / 450) * 1.5;
      this.pin
        .clear()
        .poly([0, -radius, radius, 0, 0, radius, -radius, 0])
        .stroke({ color: 0xc6a5fc, width: 2, alpha: 0.85 });
      this.pin
        .moveTo(-7, 0)
        .lineTo(7, 0)
        .moveTo(0, -7)
        .lineTo(0, 7)
        .stroke({ color: 0xe2d3fc, width: 1.5 });
    }
    if (this.world.visible && this.label.visible) return;
    this.edgeLabel.text = `ГРУППА · ${marker.label} · ${distance} м`;
    const badge = partyBadgePosition(
      safe,
      this.edgeLabel.width + 26,
      this.edgeLabel.height + 10,
      occupied,
    );
    if (!badge) return;
    this.screen.visible = true;
    this.screen.position.set(badge.x, badge.y);
    this.badgeBackground
      .clear()
      .roundRect(0, 0, badge.width, badge.height, 3)
      .fill({ color: 0x050b12, alpha: 0.95 })
      .stroke({ color: 0xc6a5fc, alpha: 0.18, width: 1 });
    this.edgeLabel.position.set(23, 5);
    const angle = Math.atan2(point.y - height / 2, point.x - width / 2);
    const cx = 9;
    const cy = badge.height / 2;
    this.direction
      .clear()
      .poly([
        cx + Math.cos(angle) * 8,
        cy + Math.sin(angle) * 8,
        cx + Math.cos(angle + 2.3) * 6,
        cy + Math.sin(angle + 2.3) * 6,
        cx + Math.cos(angle - 2.3) * 6,
        cy + Math.sin(angle - 2.3) * 6,
      ])
      .fill({ color: 0xc6a5fc, alpha: 0.9 });
  }

  destroy(): void {
    this.world.destroy({ children: true });
    this.screen.destroy({ children: true });
  }
}
